import React, { useContext, useState } from 'react';
import { ShopContext, resolveProductImage } from '../context/ShopContext';
import { CloseIcon, TrashIcon, PlusIcon, MinusIcon } from './Icons';
import { formatINR } from '../utils/currency';
import { getSizeExtraAmount } from '../utils/pricing';
import './CartDrawer.css';

const CartDrawer = ({ isOpen, onClose }) => {
  const {
    cart,
    removeFromCart,
    updateCartQuantity,
    appliedPromo,
    applyPromoCode,
    removePromoCode,
    setView,
    user,
    setAuthModalOpen,
    showToast
  } = useContext(ShopContext);

  const [promoInput, setPromoInput] = useState('');

  if (!isOpen) return null;

  // Calculate pricing math
  const getItemUnitPrice = (item) => item.unitPrice || item.price || (item.product.price + getSizeExtraAmount(item.size));
  const subtotal = cart.reduce((sum, item) => sum + getItemUnitPrice(item) * item.quantity, 0);
  const discount = appliedPromo ? subtotal * appliedPromo.discount : 0;
  const shipping = subtotal > 100 || subtotal === 0 ? 0 : 9.99;
  const total = subtotal - discount + shipping;

  const handleApplyPromo = (e) => {
    e.preventDefault();
    if (!promoInput) return;
    const success = applyPromoCode(promoInput);
    if (success) {
      setPromoInput('');
    }
  };

  const handleCheckoutClick = () => {
    if (!user) {
      onClose();
      setAuthModalOpen(true);
      showToast('Please log in or register to place your order.', 'error');
      return;
    }
    onClose();
    setView('checkout');
  };

  return (
    <div className="cart-drawer-overlay" onClick={onClose}>
      <div className="cart-drawer glass animate-slide-in" onClick={(e) => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="cart-drawer-header">
          <h3>Your Cart ({cart.reduce((sum, i) => sum + i.quantity, 0)})</h3>
          <button onClick={onClose} className="btn-icon close-drawer-btn">
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Scrollable Items list */}
        <div className="cart-drawer-items">
          {cart.length > 0 ? (
            cart.map((item, index) => {
              const uPrice = getItemUnitPrice(item);
              return (
                <div key={index} className="cart-item">
                  <img
                    src={resolveProductImage(item.product)}
                    alt={item.product?.name || 'Product'}
                    className="cart-item-img"
                    loading="eager"
                    decoding="async"
                    onError={(e) => { e.target.src = '/images/cat-slide-men-polo.JPG'; }}
                  />
                  
                  <div className="cart-item-info">
                    <h4 className="cart-item-name">{item.product.name}</h4>
                    <p className="cart-item-variant">
                      Size: <strong>{item.size}</strong> &bull; Color: <strong>{item.color}</strong>
                    </p>

                    {item.customization && (
                      <div className="cart-customization-badge">
                        <span className="cart-cust-tag">✨ Customized:</span>
                        {item.customization.printMethod && (
                          <span className="cart-cust-pill" style={{ background: '#e0e7ff', color: '#3730a3', fontWeight: 600 }}>
                            Method: <strong>{item.customization.printMethod}</strong>
                          </span>
                        )}
                        {item.customization.name && <span className="cart-cust-pill">Name: <strong>{item.customization.name}</strong></span>}
                        {item.customization.number && <span className="cart-cust-pill">No: <strong>#{item.customization.number}</strong></span>}
                        {item.customization.frontLogo && (
                          <span className="cart-cust-pill cust-img-pill">
                            Front Logo: <img src={item.customization.frontLogo} alt="Front Logo" className="cart-cust-thumb" />
                          </span>
                        )}
                        {item.customization.backLogo && (
                          <span className="cart-cust-pill cust-img-pill">
                            Back Logo: <img src={item.customization.backLogo} alt="Back Logo" className="cart-cust-thumb" />
                          </span>
                        )}
                        {!item.customization.frontLogo && !item.customization.backLogo && item.customization.image && (
                          <span className="cart-cust-pill cust-img-pill">
                            Logo: <img src={item.customization.image} alt="Logo" className="cart-cust-thumb" />
                          </span>
                        )}
                        {item.customization.notes && (
                          <span className="cart-cust-pill cart-cust-notes-pill" title={item.customization.notes}>
                            📝 Notes: <em>{item.customization.notes.length > 25 ? `${item.customization.notes.slice(0, 25)}...` : item.customization.notes}</em>
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="cart-item-qty-price">
                      {/* Qty Selector */}
                      <div className="qty-mini-selector">
                        <button 
                          onClick={() => updateCartQuantity(item.product.id, item.size, item.color, item.quantity - 1, item.customization)}
                          className="qty-mini-btn"
                        >
                          <MinusIcon size={10} />
                        </button>
                        <span className="qty-mini-val">{item.quantity}</span>
                        <button 
                          onClick={() => updateCartQuantity(item.product.id, item.size, item.color, item.quantity + 1, item.customization)}
                          className="qty-mini-btn"
                        >
                          <PlusIcon size={10} />
                        </button>
                      </div>

                      <span className="cart-item-price">
                        {formatINR(uPrice * item.quantity)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => removeFromCart(item.product.id, item.size, item.color, item.customization)}
                    className="cart-item-remove-btn"
                    title="Remove from Cart"
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              );
            })
          ) : (
            <div className="cart-empty-view">
              <div className="cart-empty-icon">🛍️</div>
              <h4>Your cart is empty</h4>
              <p>Add some premium streetwear items to start styling.</p>
              <button 
                onClick={() => { onClose(); setView('shop'); }} 
                className="btn btn-primary go-shop-btn"
              >
                Start Shopping
              </button>
            </div>
          )}
        </div>

        {/* Footer Billing calculations */}
        {cart.length > 0 && (
          <div className="cart-drawer-footer">
            {/* Coupon Code section */}
            <form onSubmit={handleApplyPromo} className="promo-code-form">
              <input
                type="text"
                placeholder="PROMO CODE (e.g. LUXE20)"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                className="input-field promo-input"
              />
              <button type="submit" className="btn btn-secondary promo-btn">
                Apply
              </button>
            </form>

            {appliedPromo && (
              <div className="active-promo-badge">
                <span>Code <strong>{appliedPromo.code}</strong> (20% Off) applied</span>
                <button type="button" onClick={removePromoCode} className="remove-promo-btn">
                  Remove
                </button>
              </div>
            )}

            {/* Calculations lines */}
            <div className="cart-totals-summary">
              <div className="totals-row">
                <span>Subtotal</span>
                <span>{formatINR(subtotal)}</span>
              </div>
              
              {appliedPromo && (
                <div className="totals-row discount-row">
                  <span>Discount</span>
                  <span>-{formatINR(discount)}</span>
                </div>
              )}

              <div className="totals-row">
                <span>Shipping</span>
                <span>{shipping === 0 ? <strong className="free-shipping-text">FREE</strong> : formatINR(shipping)}</span>
              </div>

              {shipping > 0 && (
                <p className="shipping-upsell-text">
                  Add <strong>{formatINR(100 - subtotal)}</strong> more for FREE shipping!
                </p>
              )}

              <div className="totals-row total-final-row">
                <span>Total</span>
                <span>{formatINR(total)}</span>
              </div>
            </div>

            <button onClick={handleCheckoutClick} className="btn btn-primary checkout-cta-btn">
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
